import React from 'react'
import { Banknote, MessageCircle, Phone, X } from 'lucide-react'
import {
  SUPPORT_MOBILE,
  buildWhatsAppUrl,
  dismissPaymentWarn,
  telSupportHref,
} from '../../services/managementService'

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(String(value).slice(0, 10))
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Soft reminder when pending payments are due within 5 days. App remains usable.
 */
export default function PaymentDueWarning({ portal, onClose }) {
  if (!portal) return null

  const upcoming = portal.UpcomingPayments?.length
    ? portal.UpcomingPayments
    : (portal.Payments || []).filter((p) => {
        const status = String(p.Status || '').toLowerCase()
        const days = Number(p.DaysUntilDue)
        return status === 'pending' && days >= 0 && days <= 5
      })

  const count = portal.UpcomingPaymentCount || upcoming.length
  const amount = portal.UpcomingPaymentAmount || upcoming.reduce((s, p) => s + Number(p.Amount || 0), 0)
  const days = Number(portal.DaysToNearestPayment ?? upcoming[0]?.DaysUntilDue ?? 0)
  const nearestDue = portal.NearestPaymentDue || upcoming[0]?.EffectiveDueDate || upcoming[0]?.DueDate
  const preview = upcoming.slice(0, 3)

  const dayLabel =
    days <= 0 ? 'today' : days === 1 ? 'in 1 day' : `in ${days} days`

  const waUrl = buildWhatsAppUrl({
    customerName: portal.CustomerName,
    registerCode: portal.Code,
    reason: 'Payment reminder — we have a license payment due soon.',
    amount,
    details: nearestDue
      ? `${count} payment(s) totaling ${formatMoney(amount)}; next due ${formatDate(nearestDue)} (${dayLabel}).`
      : `${count} payment(s) totaling ${formatMoney(amount)}.`,
  })

  const handleClose = () => {
    dismissPaymentWarn()
    onClose?.()
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/50">
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-sky-200 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-due-title"
      >
        <div className="bg-sky-50 px-5 py-4 border-b border-sky-100 flex items-start gap-3">
          <Banknote className="w-6 h-6 text-sky-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 id="payment-due-title" className="text-lg font-bold text-sky-950">
              Payment due soon
            </h2>
            <p className="text-sm text-sky-900/80 mt-1">
              You have {count} payment{count === 1 ? '' : 's'} due {dayLabel}. Please arrange payment
              to avoid overdue suspension.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-sky-800 hover:bg-sky-100"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Due amount
              </p>
              <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">
                {formatMoney(amount)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Next due
              </p>
              <p className="mt-1 text-base font-bold text-slate-900">{formatDate(nearestDue)}</p>
              <p className="text-xs text-slate-500 mt-0.5">{dayLabel}</p>
            </div>
          </div>

          {preview.length > 0 ? (
            <ul className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
              {preview.map((p) => (
                <li key={p.Id || `${p.FeeName}-${p.DueDate}`} className="px-3 py-2.5 flex justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{p.FeeName || 'Payment'}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {p.ContractNo || '—'}
                      {p.InstallmentNo ? ` · #${p.InstallmentNo}` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold tabular-nums text-slate-900">
                      {formatMoney(p.Amount)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDate(p.EffectiveDueDate || p.ExtendedDueDate || p.DueDate)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="flex flex-wrap gap-2 pt-1">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:brightness-95"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <a
              href={telSupportHref()}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Phone className="w-4 h-4" />
              Call {SUPPORT_MOBILE}
            </a>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 ml-auto"
            >
              Remind me later
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
